/**
 * First-login flow (plan §15): password/profile completion → administrator
 * MFA enrollment → MFA verification on later logins. Rendered by `RootGate`
 * whenever `/auth/me` reports a step other than `ready`; nothing else in the
 * application is reachable until the server reports `ready`.
 */
import { useEffect, useState } from "react";
import {
  Alert, Button, Center, Code, CopyButton, Group, List, Paper, PasswordInput, Stack, Text, TextInput, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";

import type { AuthStep } from "../../api/fleet";
import { useCompleteSetup, useLogout, useMfaActivate, useMfaEnroll, useMfaVerify } from "../../hooks/useAuth";

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  const logout = useLogout();
  return (
    <Center h="88vh" px="md">
      <Paper w="100%" maw={460} p="xl" radius="md" withBorder>
        <Stack>
          <Title order={3}>{title}</Title>
          {children}
          <Button variant="subtle" size="xs" onClick={() => logout.mutate()}>Sign out</Button>
        </Stack>
      </Paper>
    </Center>
  );
}

function CompleteSetup() {
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const setup = useCompleteSetup();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== repeat) {
      notifications.show({ color: "red", title: "Passwords differ", message: "Repeat the new password exactly." });
      return;
    }
    try {
      await setup.mutateAsync({ new_password: password, display_name: displayName });
    } catch (err) {
      notifications.show({ color: "red", title: "Setup rejected", message: (err as Error).message });
    }
  };
  return (
    <Frame title="Complete your account">
      <Text size="sm" c="dimmed">
        Your account was created with a temporary password. Choose a new one (12+ characters, mixed case, a digit)
        and set your display name. All other sessions will be signed out.
      </Text>
      <form onSubmit={(e) => void submit(e)}>
        <Stack>
          <TextInput label="Display name" value={displayName} onChange={(e) => setDisplayName(e.currentTarget.value)} required />
          <PasswordInput label="New password" value={password} onChange={(e) => setPassword(e.currentTarget.value)} required />
          <PasswordInput label="Repeat new password" value={repeat} onChange={(e) => setRepeat(e.currentTarget.value)} required />
          <Button type="submit" loading={setup.isPending}>Save and continue</Button>
        </Stack>
      </form>
    </Frame>
  );
}

function EnrollMfa() {
  const enroll = useMfaEnroll();
  const activate = useMfaActivate();
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  useEffect(() => {
    if (!enroll.data && !enroll.isPending && !enroll.isError) enroll.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await activate.mutateAsync(code);
      setRecovery(res.recovery_codes);
    } catch (err) {
      notifications.show({ color: "red", title: "Code rejected", message: (err as Error).message });
    }
  };
  if (recovery) {
    return (
      <Frame title="Recovery codes">
        <Alert color="yellow" title="Store these now">
          Each code works once if you lose your authenticator. They are not shown again.
        </Alert>
        <List size="sm" spacing={2}>{recovery.map((c) => <List.Item key={c}><Code>{c}</Code></List.Item>)}</List>
        <Group justify="space-between">
          <CopyButton value={recovery.join("\n")}>
            {({ copied, copy }) => <Button variant="light" onClick={copy}>{copied ? "Copied" : "Copy all"}</Button>}
          </CopyButton>
          <Button onClick={() => window.location.reload()}>Continue</Button>
        </Group>
      </Frame>
    );
  }
  return (
    <Frame title="Enrol an authenticator (required for administrators)">
      <Text size="sm" c="dimmed">
        Add this secret to an authenticator app (TOTP, 30 s, 6 digits), then enter the current code.
      </Text>
      {enroll.data ? (
        <Stack gap="xs">
          <Text size="xs" c="dimmed">Secret</Text>
          <Code block>{enroll.data.secret}</Code>
          <Text size="xs" c="dimmed">otpauth URI</Text>
          <Code block style={{ wordBreak: "break-all" }}>{enroll.data.otpauth}</Code>
        </Stack>
      ) : <Text size="sm">Generating secret…</Text>}
      <form onSubmit={(e) => void submit(e)}>
        <Group align="end">
          <TextInput label="Authenticator code" value={code} onChange={(e) => setCode(e.currentTarget.value)} maxLength={6} required />
          <Button type="submit" loading={activate.isPending} disabled={!enroll.data}>Activate</Button>
        </Group>
      </form>
    </Frame>
  );
}

function VerifyMfa() {
  const verify = useMfaVerify();
  const [code, setCode] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await verify.mutateAsync(code);
    } catch (err) {
      notifications.show({ color: "red", title: "Code rejected", message: (err as Error).message });
    }
  };
  return (
    <Frame title="Two-factor verification">
      <form onSubmit={(e) => void submit(e)}>
        <Stack>
          <TextInput label="Authenticator or recovery code" value={code} onChange={(e) => setCode(e.currentTarget.value)} required autoFocus />
          <Button type="submit" loading={verify.isPending}>Verify</Button>
        </Stack>
      </form>
    </Frame>
  );
}

export function AuthSetupPage({ step }: { step: AuthStep }) {
  if (step === "complete_setup") return <CompleteSetup />;
  if (step === "enroll_mfa") return <EnrollMfa />;
  if (step === "mfa") return <VerifyMfa />;
  return null;
}
